import { useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Loader2, X, Sparkles, Download, Check, FileText, MessageSquare } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function ProposalBuilder({ auditId, lead, onClose }) {
  const company = lead?.company || "";
  const industry = (lead?.sub_industry || lead?.industry || "").replace(/_/g, " ");
  const opportunities = lead?.audit_data?.report?.opportunities || [];

  const [fields, setFields] = useState({
    company_name: company,
    headline: "",
    executive_intro: "",
    why_now: "",
    cta_text: "",
    pricing: "$25,000",
    selected_opportunities: opportunities.map((_, i) => i),
  });
  const [chatPrompt, setChatPrompt] = useState("");
  const [tailoring, setTailoring] = useState(false);
  const [tailored, setTailored] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const toggleOpp = (idx) => {
    setFields(prev => {
      const sel = prev.selected_opportunities.includes(idx)
        ? prev.selected_opportunities.filter(i => i !== idx)
        : [...prev.selected_opportunities, idx].sort((a, b) => a - b);
      return { ...prev, selected_opportunities: sel };
    });
  };

  const handleTailor = async () => {
    if (!chatPrompt.trim()) return;
    setTailoring(true);
    try {
      const { data } = await api.post(`/ops/proposal/${auditId}/tailor`, { prompt: chatPrompt });
      setFields(prev => ({
        ...prev,
        headline: data.headline || prev.headline,
        executive_intro: data.executive_intro || prev.executive_intro,
        why_now: data.why_now || prev.why_now,
        cta_text: data.cta_text || prev.cta_text,
        selected_opportunities: Array.isArray(data.recommended_opportunities)
          ? data.recommended_opportunities.filter(i => i < opportunities.length)
          : prev.selected_opportunities,
      }));
      setTailored(true);
      setChatPrompt("");
    } catch (err) {
      console.error("Tailor failed:", err);
    } finally {
      setTailoring(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await api.post(`/ops/proposal/${auditId}`, fields, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Stansbury_AI_Proposal_${(fields.company_name || "Proposal").replace(/\s/g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error("Proposal download failed:", err); }
    finally { setDownloading(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-8 pb-8 overflow-y-auto"
        onClick={(e) => e.target === e.currentTarget && onClose()}>
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
          className="bg-white border border-slate-200 w-full max-w-2xl shadow-2xl" data-testid="proposal-builder-modal">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-emerald-600" />
              <div>
                <h2 className="text-base font-['Outfit'] font-medium text-[#0F172A]">Proposal Builder</h2>
                <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">{company} &middot; {industry}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-proposal-builder">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
            {/* AI Tailoring Chat */}
            <div className="bg-[#0F172A] p-5" data-testid="tailor-chat-section">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4 text-[#818CF8]" />
                <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#818CF8]">AI Tailor</span>
              </div>
              <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mb-3">
                Describe how you want to tailor this proposal. The AI will customize the headline, intro, and opportunity selection.
              </p>
              <div className="flex gap-2">
                <Textarea
                  value={chatPrompt}
                  onChange={e => setChatPrompt(e.target.value)}
                  placeholder={`e.g. "Focus on their manual outreach problem. They're a ${industry || 'mid-market'} company losing deals to faster competitors. Emphasize speed-to-revenue."`}
                  className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-none text-sm min-h-[60px] resize-none"
                  data-testid="tailor-prompt-input"
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleTailor(); } }}
                />
              </div>
              <div className="flex items-center justify-between mt-3">
                <div>
                  {tailored && (
                    <span className="flex items-center gap-1.5 text-xs text-emerald-400 font-['IBM_Plex_Sans']">
                      <Check className="w-3 h-3" /> Content tailored
                    </span>
                  )}
                </div>
                <Button
                  onClick={handleTailor}
                  disabled={tailoring || !chatPrompt.trim()}
                  className="bg-[#818CF8] text-white hover:bg-[#6366F1] rounded-none h-8 px-4 text-xs font-['Outfit']"
                  data-testid="tailor-btn">
                  {tailoring ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                  {tailoring ? "Tailoring..." : "Tailor with AI"}
                </Button>
              </div>
            </div>

            {/* Editable Fields */}
            <div className="space-y-4">
              <div>
                <label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">Company Name</label>
                <Input
                  value={fields.company_name}
                  onChange={e => setFields(p => ({ ...p, company_name: e.target.value }))}
                  className="rounded-none border-slate-300 h-9 text-sm"
                  data-testid="proposal-company-name" />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">Headline (Cover Page)</label>
                <Input
                  value={fields.headline}
                  onChange={e => setFields(p => ({ ...p, headline: e.target.value }))}
                  placeholder="Leave blank for default"
                  className="rounded-none border-slate-300 h-9 text-sm"
                  data-testid="proposal-headline" />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">Executive Intro</label>
                <Textarea
                  value={fields.executive_intro}
                  onChange={e => setFields(p => ({ ...p, executive_intro: e.target.value }))}
                  placeholder="Leave blank to use AI-generated company overview"
                  className="rounded-none border-slate-300 text-sm min-h-[60px]"
                  data-testid="proposal-exec-intro" />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">Why Now</label>
                <Textarea
                  value={fields.why_now}
                  onChange={e => setFields(p => ({ ...p, why_now: e.target.value }))}
                  placeholder="Optional urgency message specific to this prospect"
                  className="rounded-none border-slate-300 text-sm min-h-[50px]"
                  data-testid="proposal-why-now" />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">CTA Text</label>
                <Input
                  value={fields.cta_text}
                  onChange={e => setFields(p => ({ ...p, cta_text: e.target.value }))}
                  placeholder="Leave blank for default"
                  className="rounded-none border-slate-300 h-9 text-sm"
                  data-testid="proposal-cta" />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">Implementation Investment</label>
                <Input
                  value={fields.pricing}
                  onChange={e => setFields(p => ({ ...p, pricing: e.target.value }))}
                  className="rounded-none border-slate-300 h-9 text-sm"
                  data-testid="proposal-pricing" />
              </div>
            </div>

            {/* Opportunity Selection */}
            {opportunities.length > 0 && (
              <div data-testid="opportunity-selection">
                <label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-2 block">
                  Include Opportunities ({fields.selected_opportunities.length}/{opportunities.length})
                </label>
                <div className="space-y-1.5">
                  {opportunities.map((opp, i) => {
                    const isObj = typeof opp === "object";
                    const title = isObj ? opp.title : opp;
                    const value = isObj ? (opp.revenue_impact || opp.estimated_value || "") : "";
                    const selected = fields.selected_opportunities.includes(i);
                    return (
                      <button
                        key={`opp-${i}`}
                        onClick={() => toggleOpp(i)}
                        className={`w-full text-left flex items-center gap-3 p-3 border transition-colors ${selected ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                        data-testid={`opp-toggle-${i}`}>
                        <div className={`w-5 h-5 flex items-center justify-center border ${selected ? "bg-emerald-600 border-emerald-600" : "border-slate-300"}`}>
                          {selected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-['Outfit'] font-medium text-[#0F172A] truncate">{title}</p>
                        </div>
                        {value && <span className="text-xs font-['JetBrains_Mono'] text-emerald-600 flex-shrink-0">{value}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
            <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">
              {fields.selected_opportunities.length} opportunities selected
              {tailored && " \u00b7 AI tailored"}
            </p>
            <div className="flex gap-2">
              <Button onClick={onClose} variant="outline" className="rounded-none h-9 text-sm font-['Outfit']" data-testid="cancel-proposal">
                Cancel
              </Button>
              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-none h-9 px-5 text-sm font-['Outfit']"
                data-testid="download-proposal-btn">
                {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Download className="w-4 h-4 mr-1.5" />}
                {downloading ? "Generating..." : "Download Proposal"}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
